import { faFloppyDisk, faRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { Alert, Badge } from "reactstrap";

import { STORE_LOW_OXYGEN, storeInjectsN2, StoreValues } from "../../../../constants/ripeningStorePrograms";
import { RipeningCycleProgramEdit } from "../../../../hooks/useFacilityLocationQueries";
import { Button } from "../../../Helper/Button";
import StoreSettingsFields from "../Helpers/StoreSettingsFields";
import {
    buildStoreEditPayload,
    describeStoreDraft,
    draftedStore,
    formatDateTime,
    getProgramDisplayName,
    StoreDraft,
    updateStoreDraft,
    validateStoreDraft,
} from "./runningProgram";
import { RipeningProgramSnapshot } from "./ripeningProgramCycle";

interface RunningStoreEditorProps {
    program: RipeningProgramSnapshot;
    store: StoreValues;
    startAt: Dayjs;
    send: (payload: RipeningCycleProgramEdit, successMessage: string, onDone?: () => void) => void;
    saving: boolean;
    // * A change sent earlier is still waiting for the ripening room.
    changePending: boolean;
}

/** A storage program while the room stores: its store settings as one block, every one of them can change. */
export default function RunningStoreEditor({ program, store, startAt, send, saving, changePending }: RunningStoreEditorProps) {
    const [draft, setDraft] = useState<StoreDraft>({});
    const values = draftedStore(store, draft);
    const hasChanges = Object.keys(draft).length > 0;
    const errors = validateStoreDraft(draft, store);
    const summary = describeStoreDraft(draft, store);

    const onSave = () => send(buildStoreEditPayload(draft), "Storage changes sent to the ripening room", () => setDraft({}));

    return (
        <div data-testid="running-store-editor">
            <h5 className="mb-1">Program</h5>
            <div className="fw-semibold mb-2">
                {getProgramDisplayName(program)}
                <Badge color="info" className="ms-2">Storage</Badge>
            </div>
            <div className="d-flex flex-wrap gap-3 align-items-baseline mb-3">
                <div data-testid="program-started">
                    <span className="text-muted">Started: </span>
                    <span className="fw-bold">{formatDateTime(startAt)}</span>
                </div>
                <div className="text-muted" data-testid="program-ends">Stores until the cycle is ended</div>
            </div>
            {storeInjectsN2(values) && (
                <Alert color="warning" className="py-2 px-3 mb-2 small" data-testid="store-low-oxygen">
                    {"⚠ "}<strong>Low oxygen:</strong> {STORE_LOW_OXYGEN}
                </Alert>
            )}
            <div className="mb-3">
                <StoreSettingsFields
                    values={values}
                    onChange={changePending || saving ? undefined : (key, value) => setDraft((prev) => updateStoreDraft(prev, store, key, value))}
                    idPrefix="running-store"
                />
            </div>

            {hasChanges && (
                <Alert color={errors.length > 0 ? "danger" : "info"} className="mb-2" data-testid="program-change-summary">
                    {errors.length > 0 ? (
                        <ul className="mb-0 ps-3">{errors.map((error) => <li key={error}>{error}</li>)}</ul>
                    ) : (
                        <>
                            <div className="fw-semibold mb-1">This will change:</div>
                            <ul className="mb-0 ps-3">{summary.map((line) => <li key={line}>{line}</li>)}</ul>
                        </>
                    )}
                </Alert>
            )}
            <div className="d-flex gap-2 justify-content-end">
                {hasChanges && <Button color="light" icon={faRotateLeft} onClick={() => setDraft({})} disabled={saving}>Undo changes</Button>}
                <Button
                    color="primary"
                    icon={faFloppyDisk}
                    onClick={onSave}
                    isLoading={saving}
                    disabled={!hasChanges || errors.length > 0 || saving || changePending}
                >
                    Save program changes
                </Button>
            </div>
        </div>
    );
}
